/* ============================================================
 * <lion-slideshow> — auto-advancing carousel with arrows/dots/pause
 * Vanilla custom element. WCAG: respects prefers-reduced-motion + pause on hover/focus.
 * ============================================================ */

class LionSlideshow extends HTMLElement {
  constructor() {
    super();
    this.slides = [];
    this.dots = [];
    this.currentIndex = 0;
    this.timerId = null;
    this.paused = false;
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll("[data-slide]"));
    this.dots = Array.from(this.querySelectorAll("[data-slideshow-dot]"));
    this.slides.forEach((slide, i) => {
      if (i === 0) slide.classList.add("is-active");
      else slide.classList.remove("is-active");
    });

    if (this.slides.length <= 1) return;

    this.autoplay = this.dataset.autoplay === "true";
    this.interval = Math.max(
      3000,
      parseInt(this.dataset.interval || "6", 10) * 1000,
    );
    this.transitionType = this.dataset.transitionType || "cross-fade";
    this.transitionDurationMs = Math.round(
      parseFloat(this.dataset.transitionDuration || "0.7") * 1000,
    );

    this.bindControls();
    this.bindKeyboard();
    this.bindHoverPause();
    this.bindVisibilityPause();
    this.bindSwipe();

    if (this.autoplay && !this.prefersReducedMotion()) {
      this.start();
    }
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  }

  prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  bindControls() {
    const prev = this.querySelector("[data-slideshow-prev]");
    const next = this.querySelector("[data-slideshow-next]");
    const pause = this.querySelector("[data-slideshow-pause]");

    if (prev)
      prev.addEventListener("click", () =>
        this.go(this.currentIndex - 1, true),
      );
    if (next)
      next.addEventListener("click", () =>
        this.go(this.currentIndex + 1, true),
      );
    if (pause) pause.addEventListener("click", () => this.togglePause(pause));

    this.dots.forEach((dot) => {
      dot.addEventListener("click", () => {
        const idx = parseInt(dot.dataset.index || "0", 10);
        this.go(idx, true);
      });
    });
  }

  bindKeyboard() {
    this.addEventListener("keydown", (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        this.go(this.currentIndex - 1, true);
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        this.go(this.currentIndex + 1, true);
      }
    });
  }

  bindHoverPause() {
    this.addEventListener("mouseenter", () => this.stop());
    this.addEventListener("mouseleave", () => {
      if (this.autoplay && !this.paused) this.start();
    });
    this.addEventListener("focusin", () => this.stop());
    this.addEventListener("focusout", (e) => {
      if (!this.contains(e.relatedTarget) && this.autoplay && !this.paused)
        this.start();
    });
  }

  bindVisibilityPause() {
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  }

  onVisibilityChange() {
    if (document.hidden) this.stop();
    else if (this.autoplay && !this.paused) this.start();
  }

  bindSwipe() {
    let startX = 0;
    let deltaX = 0;
    this.addEventListener(
      "touchstart",
      (e) => {
        startX = e.touches[0].clientX;
        deltaX = 0;
      },
      { passive: true },
    );
    this.addEventListener(
      "touchmove",
      (e) => {
        deltaX = e.touches[0].clientX - startX;
      },
      { passive: true },
    );
    this.addEventListener("touchend", () => {
      if (Math.abs(deltaX) > 50)
        this.go(this.currentIndex + (deltaX < 0 ? 1 : -1), true);
    });
  }

  go(index, userInitiated = false) {
    const total = this.slides.length;
    const next = ((index % total) + total) % total;
    if (next === this.currentIndex) return;

    const current = this.slides[this.currentIndex];
    const nextSlide = this.slides[next];

    // Directional transitions need is-leaving on outgoing slide
    if (this.transitionType === "slide-left" || this.transitionType === "slide-right") {
      current.classList.add("is-leaving");
      window.setTimeout(() => current.classList.remove("is-leaving"), this.transitionDurationMs + 50);
    }

    current.setAttribute("aria-hidden", "true");
    current.classList.remove("is-active");
    nextSlide.removeAttribute("aria-hidden");
    nextSlide.classList.add("is-active");

    this.dots.forEach((dot, i) =>
      dot.setAttribute("aria-selected", i === next ? "true" : "false"),
    );

    this.currentIndex = next;

    if (userInitiated && this.autoplay && !this.paused) {
      this.stop();
      this.start();
    }
  }

  start() {
    this.stop();
    this.timerId = window.setInterval(
      () => this.go(this.currentIndex + 1),
      this.interval,
    );
  }

  stop() {
    if (this.timerId) {
      window.clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  togglePause(btn) {
    this.paused = !this.paused;
    if (this.paused) {
      this.stop();
      btn.setAttribute("aria-label", btn.dataset.playLabel || "Play slideshow");
    } else {
      if (this.autoplay) this.start();
      btn.setAttribute(
        "aria-label",
        btn.dataset.pauseLabel || "Pause slideshow",
      );
    }
    btn.classList.toggle("is-paused", this.paused);
  }
}

if (!customElements.get("lion-slideshow")) {
  customElements.define("lion-slideshow", LionSlideshow);
}
